import { useEffect, useState } from 'react'
import { LANGS, type Lang } from '@/core/i18n'
import { clearProgress } from '@/core/progress'
import { MONDPHASEN, type IalaRegion, type Units, type ViewMode } from '@/core/settings'
import { offlineState, type OfflineState } from '@/pwa/register'
import { useSettings } from './settings'

const MODES: ViewMode[] = ['night', 'day', 'red']
const UNITS: Units[] = ['nm', 'km']

const REGIONS: IalaRegion[] = ['A', 'B']

const OFFLINE_KEYS = {
  ready: 'settings.offline.ready',
  pending: 'settings.offline.pending',
  off: 'settings.offline.off',
} as const

/** Einstellungen: Sprache, Region, Einheiten, Ansicht, und was auf dem Gerät liegt. */
export function SettingsView() {
  const { t, settings, update } = useSettings()
  const [cleared, setCleared] = useState(false)
  const [offline, setOffline] = useState<OfflineState>('pending')

  useEffect(() => {
    let alive = true
    void offlineState().then((state) => {
      if (alive) setOffline(state)
    })
    return () => {
      alive = false
    }
  }, [])

  return (
    <section className="settings" data-testid="settings">
      <h2>{t('settings.title')}</h2>

      <div className="settings__row">
        <span className="settings__label">{t('settings.language')}</span>
        <span className="modes">
          {LANGS.map((lang: Lang) => (
            <button
              key={lang}
              type="button"
              className={`modes__item${settings.lang === lang ? ' modes__item--on' : ''}`}
              data-lang={lang}
              onClick={() => update({ lang })}
            >
              {t(lang === 'de' ? 'settings.language.de' : 'settings.language.en')}
            </button>
          ))}
        </span>
      </div>

      <div className="settings__row">
        <span className="settings__label">{t('settings.region')}</span>
        <span className="modes">
          {REGIONS.map((region) => (
            <button
              key={region}
              type="button"
              className={`modes__item${settings.region === region ? ' modes__item--on' : ''}`}
              data-setting-region={region}
              onClick={() => update({ region })}
            >
              {t('region.button', { region })}
            </button>
          ))}
        </span>
      </div>
      <p className="signal__legend">{t('settings.region.hint')}</p>

      <div className="settings__row">
        <span className="settings__label">{t('settings.units')}</span>
        <span className="modes">
          {UNITS.map((units) => (
            <button
              key={units}
              type="button"
              className={`modes__item${settings.units === units ? ' modes__item--on' : ''}`}
              data-units={units}
              onClick={() => update({ units })}
            >
              {t(units === 'nm' ? 'settings.units.nm' : 'settings.units.km')}
            </button>
          ))}
        </span>
      </div>

      {/* Fünf Stufen statt eines stufenlosen Reglers: die Mondphase ist eine Lage, die
          man erkennt, kein Wert, den man einstellt. Neumond und Vollmond sind die
          Extreme, und dazwischen liegt, was man nachts wirklich unterscheidet. */}
      <div className="settings__row">
        <span className="settings__label">{t('settings.mondphase')}</span>
        <span className="modes">
          {MONDPHASEN.map(([wert, schluessel]) => (
            <button
              key={schluessel}
              type="button"
              className={`modes__item${Math.abs(settings.mondphase - wert) < 0.01 ? ' modes__item--on' : ''}`}
              data-mondphase={wert}
              onClick={() => update({ mondphase: wert })}
            >
              {t(schluessel as never)}
            </button>
          ))}
        </span>
      </div>
      <p className="signal__legend">{t('settings.mondphase.hint')}</p>

      <div className="settings__row">
        <span className="settings__label">{t('settings.view')}</span>
        <span className="modes">
          {MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              className={`modes__item${settings.mode === mode ? ' modes__item--on' : ''}`}
              data-setting-mode={mode}
              onClick={() => update({ mode })}
            >
              {t(mode === 'night' ? 'mode.night' : mode === 'day' ? 'mode.day' : 'mode.red')}
            </button>
          ))}
        </span>
      </div>

      <p className="signal__legend" data-testid="settings-offline">
        {t(OFFLINE_KEYS[offline])}
      </p>
      <p className="signal__legend">{t('settings.storage')}</p>

      <button
        type="button"
        className="toggle"
        data-testid="settings-reset"
        onClick={() => {
          clearProgress()
          setCleared(true)
        }}
      >
        {t('settings.reset')}
      </button>
      {cleared && (
        <p className="signal__legend" data-testid="settings-reset-done">
          {t('settings.reset.done')}
        </p>
      )}
    </section>
  )
}
